import React, { useState, useEffect,useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { InputLabel, Select, MenuItem } from '@mui/material';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import CheckIcon from '@mui/icons-material/Check';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';

import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';


export const WarrantyAllowanceAdministration = () => {
    const localized = useStorefrontAdministrationConfig().labels;
    const [rows, setRows] = useState([]);
    const [isAllowance, setIsAllowance] = useState(true);
    const storeData = useStorefrontSettings();
    const [fetching, setFetching] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "DuplicateKeyViolation": localized.WarrantyAllowanceDuplicateKeyViolation,
        "GenericFailure": localized.WarrantyAllowanceGenericFailure,
        "DeleteSuccess": localized.WarrantyAllowanceSuccessfulDelete,
        "UpdateSuccess": localized.WarrantyAllowanceSuccessfulUpdate,
        "AddSuccess": localized.WarrantyAllowanceSuccessfulAdd,
        "InvalidCustomerNumber": localized.InvalidCustomerNumber
    };

    useEffect(() => {
        getCustomerWarrantyAllowances();
    }, []);


    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' },
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                {renderAddButton()}
            </GridToolbarContainer>
        );
    }

    const getCustomerWarrantyAllowances = () => {
        setFetching(true);
        Storefront.getWarrantyAllowances(storeData.storefrontNumber).then(loaded);
    }

    const updateWarrantyAllowances = async (form, type) => {
        var request = {
            storefrontNumber: storeData.storefrontNumber,
            customerNumber: form.id,
            isWarrantyAllowance: form.isAllowance,
            type: type
        };
        return await Storefront.updateWarrantyAllowanceCustomer(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.WarrantyAllowanceErrorNotification);
                }
                getCustomerWarrantyAllowances();
            }
        });
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setFetching(false);
    }

    const mapRows = (data) => {
        var rows = [];
        data.warrantyAllowanceCustomers.forEach((customerWarrantyAllowance) => {

            rows.push({
                id: customerWarrantyAllowance.customerNumber,
                customerName: customerWarrantyAllowance.customerName,
                isAllowance: customerWarrantyAllowance.isWarrantyAllowance
            });
        });
        setRows(rows);
    }

    const handleHasAllowanceChange = (event) => {
        setIsAllowance(event.target.value);
    }

    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setIsAllowance(row.isAllowance);
        setSelectedRow(row);
        setOpenUpdate(true);
    };    

    const handleCloseUpdate = () => {
        setOpenUpdate(false);
    };

    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleClickOpenAdd = (e) => {
        e.stopPropagation();
        setOpenAdd(true);
    };


    const handleCloseAdd = () => {
        setOpenAdd(false);
        setIsAllowance(true);
    };

    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{localized.AreYouSureWarrantyAllowanceDelete}</h3>
                    <strong>{`${row.id} - ${row.customerName}`}</strong>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("id", row.id);
                const formJson = Object.fromEntries(formData.entries());
                updateWarrantyAllowances(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );

    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.UpdateWarrantyAllowance}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("id", row.id);
                const formJson = Object.fromEntries(formData.entries());
                updateWarrantyAllowances(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "error" }}
            maxWidth="sm"
            fullWidth
        >
            <h3>{localized.ValuesEligibleForUpdateBelow}</h3>

            <InputLabel id="isAllowance-label">{localized.HasWarrantyAllowance}</InputLabel>
            <Select
                required
                fullWidth
                labelId="isAllowance-label"
                id="isAllowance"
                name="isAllowance"
                value={isAllowance}
                label={localized.HasWarrantyAllowance}
                onChange={handleHasAllowanceChange}
                MenuProps={{
                    disablePortal: true,
                    PaperProps: {
                        sx: { zIndex: 1600 },
                    },
                }}
            >
                <MenuItem value={true}>{localized.True}</MenuItem>
                <MenuItem value={false}>{localized.False}</MenuItem>
            </Select>
        </MUIModal>
    );


    const renderAddButton = () => (
        <>
            <strong>
                <Button
                    variant="standard"
                    color="success"
                    style={{ marginLeft: 16 }}
                    onClick={handleClickOpenAdd}
                >
                    {localized.StorefrontAdminAdd}
                </Button>
            </strong>

            <MUIModal
                open={openAdd}
                onClose={handleCloseAdd}
                title={localized.AddWarrantyAllowance}
                onSubmit={(event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries(formData.entries());
                    updateWarrantyAllowances(formJson, "add");
                    handleCloseAdd();
                }}
                submitLabel={localized.StorefrontAdminAdd}
                cancelLabel={localized.StorefrontAdminCancel}
                showDefaultButtons={true}
                submitButtonProps={{ color: "success" }}
                cancelButtonProps={{ color: "error" }}
                maxWidth="sm"
                fullWidth
            >
                <h3>{localized.FillRequiredFieldsBelow}</h3>

                <InputLabel id="customerNumber-label">{localized.CustomerNumber}</InputLabel>
                <TextField
                    inputProps={{ maxLength: 8 }}
                    required
                    margin="dense"
                    id="id"
                    name="id"
                    label={localized.CustomerNumber}
                    type="number"
                    fullWidth
                    variant="standard"
                />

                <InputLabel id="isAllowance-label">{localized.HasWarrantyAllowance}</InputLabel>
                <Select
                    required
                    fullWidth
                    labelId="isAllowance-label"
                    id="isAllowance"
                    name="isAllowance"
                    value={isAllowance}
                    label={localized.HasWarrantyAllowance}
                    onChange={handleHasAllowanceChange}
                    MenuProps={{
                        disablePortal: true,
                        PaperProps: {
                            sx: { zIndex: 1600 },
                        },
                    }}
                >
                    <MenuItem value={true}>{localized.True}</MenuItem>
                    <MenuItem value={false}>{localized.False}</MenuItem>
                </Select>
            </MUIModal>
        </>
    );

    const columns = [
        {
            field: 'id',
            headerName: localized.CustomerNumber,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'customerName',
            headerName: localized.CustomerAddressName,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'isAllowance',
            headerName: localized.HasWarrantyAllowance,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params) => {
                return (
                    <strong>
                        {params.value ? <CheckIcon /> : null}
                    </strong>
                )
            }
        },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true

        },
        {
            field: 'deleteButton',
            headerName: localized.StorefrontAdminDelete,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const warrentyItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length >0) && 
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.WarrantyAllowancesAdministrationHeader}</Typography>
                        {warrentyItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddButton()}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default WarrantyAllowanceAdministration;

